import Phaser from 'phaser';
import { App } from './App';
import {
  COLORS,
  FONTS,
  PAYMENTS_WINDOW,
  TASKBAR,
  toHexColor,
} from '../config/gameConfig';
import type { BadgePayload, EventBus, PendingPayment } from '../systems/EventBus';
import { Scrollable } from '../ui/Scrollable';

interface PaymentsAppOptions {
  getNowMs: () => number;
}

export class PaymentsApp extends App {
  private listContainer!: Phaser.GameObjects.Container;
  private scrollable!: Scrollable;
  private pending: PendingPayment[] = [];
  private pulseActive = false;
  private pulseTimer: Phaser.Time.TimerEvent | null = null;
  private postUpdate: (() => void) | null = null;

  private readonly onPaymentQueued = (p: { payment: PendingPayment }) => {
    this.pending = [p.payment, ...this.pending];
    this.rerender();
    this.triggerPulse();
    this.emitBadge();
  };

  private readonly onCashApplicationSent = (p: { wireId: string }) => {
    this.removeById(p.wireId);
  };

  private readonly onCashApplicationClosed = (p: { wireId: string }) => {
    this.removeById(p.wireId);
  };

  constructor(
    scene: Phaser.Scene,
    bus: EventBus,
    private readonly opts: PaymentsAppOptions,
  ) {
    super(scene, bus, {
      x: PAYMENTS_WINDOW.x,
      y: PAYMENTS_WINDOW.y,
      width: PAYMENTS_WINDOW.width,
      height: PAYMENTS_WINDOW.height,
      title: 'Payments',
      windowId: 'payments',
      titleBarHeight: PAYMENTS_WINDOW.titleBarHeight,
    });
    this.renderBody();
    this.bus.on('payment:queued', this.onPaymentQueued);
    this.bus.on('cash-application:sent', this.onCashApplicationSent);
    this.bus.on('cash-application:closed', this.onCashApplicationClosed);

    // Per-frame: tick countdowns + expire payments past their TTL.
    this.postUpdate = () => this.tick();
    scene.events.on(Phaser.Scenes.Events.POST_UPDATE, this.postUpdate);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());

    this.emitBadge();
  }

  getBadge(): BadgePayload {
    return {
      label: 'Payments',
      count: this.pending.length,
      pulse: this.pulseActive,
      iconColor: COLORS.success,
    };
  }

  private emitBadge(): void {
    this.bus.emit('window:badge-changed', { windowId: this.options.windowId, badge: this.getBadge() });
  }

  private triggerPulse(): void {
    this.pulseActive = true;
    this.emitBadge();
    if (this.pulseTimer) this.pulseTimer.remove();
    this.pulseTimer = this.scene.time.delayedCall(TASKBAR.pulseHoldAfterEventMs, () => {
      this.pulseActive = false;
      this.pulseTimer = null;
      this.emitBadge();
    });
  }

  protected onRestored(): void {
    this.pulseActive = false;
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.emitBadge();
  }

  protected onResize(): void {
    this.scrollable?.resize(this.bodyWidth, this.bodyHeight);
    this.rerender();
  }

  protected renderBody(): void {
    this.listContainer = this.scene.add.container(0, 0);
    this.bodyContainer.add(this.listContainer);
    this.scrollable = new Scrollable(this.scene, this, this.listContainer, this.bodyContainer);
    this.rerender();
  }

  private removeById(wireId: string): void {
    const before = this.pending.length;
    this.pending = this.pending.filter((p) => p.id !== wireId);
    if (this.pending.length !== before) {
      this.rerender();
      this.emitBadge();
    }
  }

  private tick(): void {
    if (this.pending.length === 0) return;
    const now = this.opts.getNowMs();
    const expired: PendingPayment[] = [];
    for (const p of this.pending) {
      if (now >= p.expiresAtMs) expired.push(p);
    }
    if (expired.length > 0) {
      this.pending = this.pending.filter((p) => !expired.some((e) => e.id === p.id));
      for (const p of expired) {
        this.bus.emit('payment:expired', { payment: p });
      }
      this.rerender();
      this.emitBadge();
    }
    // Live age update: re-render every ~1 sec to keep the age counter ticking.
    if (now - this.lastAgeRender > 1000) {
      this.lastAgeRender = now;
      this.rerender();
    }
  }

  private lastAgeRender = 0;

  private rerender(): void {
    if (!this.listContainer) return;
    this.listContainer.removeAll(true);

    if (this.pending.length === 0) {
      const empty = this.scene.add.text(
        PAYMENTS_WINDOW.rowPaddingX,
        PAYMENTS_WINDOW.rowPaddingY,
        'no payments waiting',
        {
          fontFamily: FONTS.ui,
          fontSize: '12px',
          color: toHexColor(COLORS.textMuted),
          fontStyle: 'italic',
        },
      );
      this.listContainer.add(empty);
      this.scrollable?.setContentHeight(40);
      this.refreshChildDepths();
      return;
    }

    const now = this.opts.getNowMs();
    let y = 4;
    for (const p of this.pending) {
      const ageSec = Math.max(0, Math.floor((now - p.arrivedAtMs) / 1000));
      const remainingSec = Math.max(0, Math.floor((p.expiresAtMs - now) / 1000));
      const isUrgent = remainingSec < 30;

      // Row background
      const bg = this.scene.add
        .rectangle(0, y, this.bodyWidth, PAYMENTS_WINDOW.rowHeight, COLORS.windowChrome, 0.6)
        .setOrigin(0, 0)
        .setStrokeStyle(1, isUrgent ? COLORS.accentDanger : COLORS.windowBorder, 0.6);
      this.listContainer.add(bg);

      // Amount
      const amount = this.scene.add.text(
        PAYMENTS_WINDOW.rowPaddingX,
        y + PAYMENTS_WINDOW.rowPaddingY,
        `$${p.amount.toLocaleString('en-US')}`,
        {
          fontFamily: FONTS.ui,
          fontSize: PAYMENTS_WINDOW.amountFontSize,
          color: toHexColor(COLORS.textPrimary),
          fontStyle: 'bold',
        },
      );
      this.listContainer.add(amount);

      // Age (top-right of row)
      const ageText = this.scene.add.text(
        this.bodyWidth - PAYMENTS_WINDOW.rowPaddingX - PAYMENTS_WINDOW.buttonWidth - 8,
        y + PAYMENTS_WINDOW.rowPaddingY + 2,
        `${ageSec}s ago`,
        {
          fontFamily: FONTS.ui,
          fontSize: PAYMENTS_WINDOW.ageFontSize,
          color: toHexColor(isUrgent ? COLORS.accentDanger : COLORS.textMuted),
        },
      ).setOrigin(1, 0);
      this.listContainer.add(ageText);

      // Remittance hint
      const hint = this.scene.add.text(
        PAYMENTS_WINDOW.rowPaddingX,
        y + PAYMENTS_WINDOW.rowPaddingY + 22,
        p.remittance,
        {
          fontFamily: FONTS.ui,
          fontSize: PAYMENTS_WINDOW.customerFontSize,
          color: toHexColor(COLORS.textMuted),
          wordWrap: { width: this.bodyWidth - PAYMENTS_WINDOW.rowPaddingX * 2 - PAYMENTS_WINDOW.buttonWidth - 12 },
        },
      );
      this.listContainer.add(hint);

      // Apply button (right-aligned)
      const btnX = this.bodyWidth - PAYMENTS_WINDOW.rowPaddingX - PAYMENTS_WINDOW.buttonWidth;
      const btnY = y + PAYMENTS_WINDOW.rowHeight - PAYMENTS_WINDOW.buttonHeight - PAYMENTS_WINDOW.rowPaddingY;
      const btnBg = this.scene.add
        .rectangle(btnX, btnY, PAYMENTS_WINDOW.buttonWidth, PAYMENTS_WINDOW.buttonHeight, COLORS.buttonPrimary)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      const btnLabel = this.scene.add.text(
        btnX + PAYMENTS_WINDOW.buttonWidth / 2,
        btnY + PAYMENTS_WINDOW.buttonHeight / 2,
        'Apply',
        {
          fontFamily: FONTS.ui,
          fontSize: PAYMENTS_WINDOW.buttonFontSize,
          color: toHexColor(COLORS.buttonPrimaryText),
          fontStyle: 'bold',
        },
      ).setOrigin(0.5, 0.5);
      this.listContainer.add(btnBg);
      this.listContainer.add(btnLabel);

      const payment = p;
      btnBg.on('pointerdown', () => {
        this.bus.emit('cash-application:open-for', { payment });
      });

      y += PAYMENTS_WINDOW.rowHeight + 6;
    }

    this.scrollable?.setContentHeight(y + 8);
    this.refreshChildDepths();
  }

  private teardown(): void {
    this.bus.off('payment:queued', this.onPaymentQueued);
    this.bus.off('cash-application:sent', this.onCashApplicationSent);
    this.bus.off('cash-application:closed', this.onCashApplicationClosed);
    if (this.postUpdate) {
      this.scene.events.off(Phaser.Scenes.Events.POST_UPDATE, this.postUpdate);
      this.postUpdate = null;
    }
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.scrollable?.destroy();
  }
}
